/**
 * Before a PDF export: the brand details its template prints must be filled
 * in. `ensure(spec)` resolves at once when they are; otherwise it asks for
 * the missing ones only (BrandDetailsDialog) and resolves with what was
 * entered — the export uses it right away — or null when cancelled.
 */

import { useCallback, useRef, useState } from 'react';
import type { PrintTemplateSpec } from 'shared-types';
import type { TemplateLibraryConfigFieldsFragment } from '../../types/graphqlTypes';
import { brandDetailsUsedBy, missingBrandDetails, type BrandDetail } from './brandDetails';
import { BrandDetailsDialog, type BrandDetailsResult } from './BrandDetailsDialog';

const NOTHING_NEW: BrandDetailsResult = { brand: {}, logoUrl: null };

export function useBrandDetailsGate(library: { organisationId: string; canEdit: boolean; config: TemplateLibraryConfigFieldsFragment } | null | undefined) {
  const [asking, setAsking] = useState<{ details: BrandDetail[]; key: number } | null>(null);
  const settle = useRef<((result: BrandDetailsResult | null) => void) | null>(null);

  const config = library?.config ?? null;
  const ensure = useCallback(
    (spec: PrintTemplateSpec): Promise<BrandDetailsResult | null> => {
      if (!config) return Promise.resolve(NOTHING_NEW);
      const missing = missingBrandDetails(config, brandDetailsUsedBy(spec));
      if (missing.length === 0) return Promise.resolve(NOTHING_NEW);
      settle.current?.(null);
      return new Promise((resolve) => {
        settle.current = resolve;
        setAsking({ details: missing, key: Date.now() });
      });
    },
    [config],
  );

  const finish = (result: BrandDetailsResult | null) => {
    settle.current?.(result);
    settle.current = null;
    setAsking(null);
  };

  const dialog =
    library && asking ? (
      <BrandDetailsDialog
        key={asking.key}
        open
        organisationId={library.organisationId}
        config={library.config}
        details={asking.details}
        reason="export"
        canEdit={library.canEdit}
        onDone={finish}
        onCancel={() => finish(null)}
        onSkip={() => finish(NOTHING_NEW)}
      />
    ) : null;

  return { ensure, dialog };
}
